// ai-utils.js
import axios from 'axios';
import { doc, getDoc, updateDoc, increment, setDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { runTransaction } from 'firebase/firestore';
import API_CONFIG from '../../config/api';

// Constants for AI feature access
export const AI_FEATURE_CONFIG = {
  TRIAL_LIMIT: 10,
  POINTS_REQUIRED: 15000,
  TRIAL_PERIOD_DAYS: 60
};

// Configure base URL for all axios requests
export const apiClient = axios.create({
  baseURL: API_CONFIG.getBaseUrl(),
  timeout: 30000, // 30 second timeout
  headers: {
    'Content-Type': 'application/json'
  },
  withCredentials: false // Ensure we don't send credentials for CORS requests
});

// Update API client configuration
apiClient.defaults.headers.common['Access-Control-Allow-Origin'] = '*';
apiClient.defaults.headers.common['Access-Control-Allow-Methods'] = 'GET, POST, PUT, DELETE, OPTIONS';
apiClient.defaults.headers.common['Access-Control-Allow-Headers'] = 'X-Requested-With, Content-Type, Authorization';

// Add interceptor to handle CORS preflight
apiClient.interceptors.request.use(config => {
  // Add credentials only for same-origin requests
  config.withCredentials = config.url.startsWith(window.location.origin);
  return config;
});

// Add error interceptor to handle CORS errors
apiClient.interceptors.response.use(
  response => response,
  error => {
    if (error.response && error.response.status === 403) {
      const retryConfig = error.config;
      retryConfig.headers = {
        ...retryConfig.headers,
        'Access-Control-Allow-Origin': '*',
        'Access-Control-Allow-Methods': 'GET, POST, PUT, DELETE, OPTIONS',
        'Access-Control-Allow-Headers': 'X-Requested-With, Content-Type, Authorization'
      };
      return apiClient(retryConfig);
    }
    return Promise.reject(error);
  }
);

// Helper function to check if server is running
const isServerRunning = async () => {
  try {
    // Check if we're in production
    if (process.env.NODE_ENV === 'production') {
      // In production, just try the API endpoint directly
      try {
        const response = await axios.get(API_CONFIG.getUrl('api/test'), { 
          timeout: 3000,
          headers: {
            'Content-Type': 'application/json'
          },
          withCredentials: false
        });
        return true;
      } catch (err) {
        // Check if we got any response - a 404 is still a running server
        if (err.response) {
          return true;
        }
        return false;
      }
    }
    
    // For development, try both server ports and alternative endpoints
    const possibleServers = [
      API_CONFIG.getBaseUrl(),
      'http://localhost:5001',
      'http://localhost:3001',
      'http://localhost:8080'
    ];
    
    const possibleEndpoints = [
      '/api/test',
      '/api/ai/status',
      '/',
      '/api'
    ];
    
    // Try each server
    for (const server of possibleServers) {
      for (const endpoint of possibleEndpoints) {
        try {
          // Use axios directly with specific CORS settings
          const response = await axios.get(`${server}${endpoint}`, { 
            timeout: 3000,
            headers: {
              'Content-Type': 'application/json'
            },
            withCredentials: false
          });
          
          // Extract the port from the server URL and store it
          const portMatch = server.match(/:(\d+)$/);
          if (portMatch && portMatch[1]) {
            const port = portMatch[1];
            sessionStorage.setItem('api_port', port);
            apiClient.defaults.baseURL = server;
          }
          
          // If we find a working server, update the apiClient baseURL
          apiClient.defaults.baseURL = server;
          return true;
        } catch (err) {
          if (err.code === 'ECONNREFUSED' || !err.response) {
            continue;
          } else if (err.response && err.response.status !== 404) {
            // Got a response that's not 404, so server is running
            const portMatch = server.match(/:(\d+)$/);
            if (portMatch && portMatch[1]) {
              const port = portMatch[1];
              sessionStorage.setItem('api_port', port);
              apiClient.defaults.baseURL = server;
            }
            
            apiClient.defaults.baseURL = server;
            return true;
          }
        }
      }
    }
    
    return false;
  } catch (err) {
    return false;
  }
};

// Add a function to verify the apiClient baseURL is correct
export const verifyApiClientSetup = async () => {
  try {
    // Get the current baseURL from apiClient
    const currentBaseURL = apiClient.defaults.baseURL;
    
    // Check if there's a cached port and if baseURL matches it
    const cachedPort = sessionStorage.getItem('api_port');
    if (cachedPort) {
      const expectedBaseURL = `http://localhost:${cachedPort}`;
      
      // Force update if they don't match
      if (currentBaseURL !== expectedBaseURL) {
        apiClient.defaults.baseURL = expectedBaseURL;
      }
    }
    
    // Perform a simple test request
    try {
      await apiClient.get('/api/test');
      return true;
    } catch (err) {
      // If test request fails, re-run server detection
      const serverRunning = await isServerRunning();
      if (serverRunning) {
        return true;
      }
      return false;
    }
  } catch (err) {
    return false;
  }
};

// Check if the user has unlocked AI features
export const hasUnlockedAIFeatures = async (userId) => {
  try {
    if (!userId) return { hasAccess: false };
    
    // First check if the user has a feature document in the subcollection
    const featureDoc = await getDoc(doc(db, 'users', userId, 'features', 'ai_features_unlock'));
    if (featureDoc.exists()) {
      const featureData = featureDoc.data();
      
      // Check if user has valid purchased access via subcollection
      if (featureData?.unlocked && featureData?.expiryDate) {
        const expiryDate = new Date(featureData.expiryDate);
        if (expiryDate > new Date()) {
          return {
            hasAccess: true,
            isPurchased: true,
            expiryDate: featureData.expiryDate,
            currentPoints: 0,
            source: 'subcollection'
          };
        }
      }
    }
    
    // If not found in subcollection, check in the features array (admin granted or via rewards)
    const userDoc = await getDoc(doc(db, 'users', userId));
    if (userDoc.exists()) {
      const userData = userDoc.data();
      
      // Check for feature in features array (used by admin feature grant and reward system)
      if (userData.features && Array.isArray(userData.features)) {
        const aiFeature = userData.features.find(f => f.id === 'ai_features_unlock');
        if (aiFeature && aiFeature.expiryDate) {
          const expiryDate = new Date(aiFeature.expiryDate);
          if (expiryDate > new Date()) {
            return {
              hasAccess: true,
              isPurchased: true,
              expiryDate: aiFeature.expiryDate,
              currentPoints: userData.points || 0,
              source: 'features_array',
              grantedAt: aiFeature.grantedAt || null,
              isAdminGranted: !aiFeature.purchasedWithPoints
            };
          }
        }
      }
      
      // If no valid feature found in array, proceed with trial check
      const usageDoc = await getDoc(doc(db, 'users', userId, 'features', 'ai_generation'));
      const usageData = usageDoc.exists() ? usageDoc.data() : { usageCount: 0 };
      const points = userData.points || 0;
      
      // Check trial usage - ensure we're not exceeding the limit
      const trialUsageCount = usageData.usageCount || 0;
      const hasTrialsLeft = trialUsageCount < AI_FEATURE_CONFIG.TRIAL_LIMIT;
      
      return {
        hasAccess: true,
        isPurchased: false,
        isTrial: hasTrialsLeft,
        trialUsageCount,
        trialsRemaining: Math.max(0, AI_FEATURE_CONFIG.TRIAL_LIMIT - trialUsageCount),
        currentPoints: points,
        requiredPoints: AI_FEATURE_CONFIG.POINTS_REQUIRED,
        lastUsed: usageData.lastUsed || null,
        reason: hasTrialsLeft 
          ? 'Trial available' 
          : (points >= AI_FEATURE_CONFIG.POINTS_REQUIRED ? 'Sufficient points' : 'Insufficient points and no trials remaining')
      };
    }
    
    // If we couldn't determine access, try API endpoints
    const endpoints = [
      `/api/user/checkFeatureAccess?userId=${userId}&featureId=ai_features_unlock`,
      `/api/ai/user/checkFeatureAccess?userId=${userId}&featureId=ai_features_unlock`
    ];
    
    let response = null;
    let error = null;
    
    // Try each endpoint until one works
    for (const endpoint of endpoints) {
      try {
        response = await apiClient.get(endpoint);
        if (response.data) {
          return response.data;
        }
      } catch (err) {
        error = err;
      }
    }
    
    if (!response) {
      return { 
        hasAccess: false, 
        reason: 'API unavailable',
        error: error?.message || 'Unknown error'
      };
    }
    
    return { hasAccess: false, reason: 'Could not determine access status' };
  } catch (error) {
    return { hasAccess: false, error: error.message };
  }
};

// Test function to check if API is reachable
export const testAPIConnection = async () => {
  try {
    // First check if server is running at all
    const serverUp = await isServerRunning();
    if (!serverUp) {
      return { success: false, error: 'Server not running' };
    }
    
    // Try the test endpoint
    try {
      const response = await apiClient.get('/api/test');
      return { success: true, data: response.data };
    } catch (error) {
      // If the test endpoint fails but server is running, consider it a partial success
      return { success: true, partial: true, error: error.message };
    }
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// Get the user's AI generation usage
export const getAIGenerationUsage = async (userId) => {
  try {
    if (!userId) return { usageCount: 0, maxFreeUsage: 10, showReminder: true };
    
    const userAIUsageRef = doc(db, 'users', userId, 'features', 'ai_generation');
    const docSnap = await getDoc(userAIUsageRef);
    
    if (docSnap.exists()) {
      const data = docSnap.data();
      return {
        usageCount: data.usageCount || 0,
        maxFreeUsage: 10,
        showReminder: data.showReminder !== false
      };
    } else {
      // Initialize the document if it doesn't exist
      await setDoc(userAIUsageRef, {
        usageCount: 0,
        showReminder: true,
        lastUsed: new Date()
      });
      return { usageCount: 0, maxFreeUsage: 10, showReminder: true };
    }
  } catch (error) {
    return { usageCount: 0, maxFreeUsage: 25, showReminder: true, error: error.message };
  }
};

// Increment the AI generation usage count
export const incrementAIGenerationUsage = async (userId) => {
  try {
    if (!userId) return { success: false, error: 'User not authenticated' };
    
    // First check if the user has purchased access via subcollection
    const featureDoc = await getDoc(doc(db, 'users', userId, 'features', 'ai_features_unlock'));
    const featureData = featureDoc.exists() ? featureDoc.data() : null;
    
    // Check if feature is valid in subcollection
    if (featureData?.unlocked && featureData?.expiryDate) {
      const expiryDate = new Date(featureData.expiryDate);
      if (expiryDate > new Date()) {
        return { 
          success: true,
          isPurchased: true,
          expiryDate: featureData.expiryDate,
          message: 'User has purchased access, trial not counted',
          source: 'subcollection'
        };
      }
    }
    
    // If not in subcollection, check in features array (admin granted or rewards)
    const userDoc = await getDoc(doc(db, 'users', userId));
    if (!userDoc.exists()) {
      return { success: false, error: 'User not found' };
    }
    
    const userData = userDoc.data();
    if (userData.features && Array.isArray(userData.features)) {
      const aiFeature = userData.features.find(f => f.id === 'ai_features_unlock');
      if (aiFeature && aiFeature.expiryDate) {
        const expiryDate = new Date(aiFeature.expiryDate);
        if (expiryDate > new Date()) {
          return { 
            success: true,
            isPurchased: true,
            expiryDate: aiFeature.expiryDate,
            message: 'User has purchased/granted access, trial not counted',
            source: 'features_array',
            isAdminGranted: !aiFeature.purchasedWithPoints
          };
        }
      }
    }
    
    // At this point, the user doesn't have purchased access, so we increment trial usage
    const userAIUsageRef = doc(db, 'users', userId, 'features', 'ai_generation');
    const usageDocSnap = await getDoc(userAIUsageRef);
    
    let currentUsage = 0;
    if (usageDocSnap.exists()) {
      currentUsage = usageDocSnap.data().usageCount || 0;
    }
    
    // Check if user has trials left
    if (currentUsage >= AI_FEATURE_CONFIG.TRIAL_LIMIT) {
      return {
        success: false,
        error: 'No trials left',
        usageCount: currentUsage,
        trialsRemaining: 0
      };
    }
    
    // Increment the usage count
    const newUsage = currentUsage + 1;
    const now = new Date();
    
    // Use a transaction to ensure atomic update
    try {
      await runTransaction(db, async (transaction) => {
        // Get the latest usage data
        const freshUsageDoc = await transaction.get(userAIUsageRef);
        const freshUsage = freshUsageDoc.exists() ? (freshUsageDoc.data().usageCount || 0) : 0;
        
        // Double-check we're not exceeding the limit
        if (freshUsage >= AI_FEATURE_CONFIG.TRIAL_LIMIT) {
          throw new Error('No trials left - detected during transaction');
        }
        
        // Update or create the document
        if (freshUsageDoc.exists()) {
          transaction.update(userAIUsageRef, {
            usageCount: freshUsage + 1,
            lastUsed: now.toISOString(),
            updatedAt: now.toISOString()
          });
        } else {
          transaction.set(userAIUsageRef, {
            usageCount: 1,
            lastUsed: now.toISOString(),
            createdAt: now.toISOString(),
            updatedAt: now.toISOString(),
            showReminder: true
          });
        }
      });
      
      return {
        success: true,
        usageCount: newUsage,
        trialsRemaining: Math.max(0, AI_FEATURE_CONFIG.TRIAL_LIMIT - newUsage),
        trialUsed: true,
        message: `Trial ${newUsage} used successfully`
      };
    } catch (txError) {
      return { 
        success: false, 
        error: txError.message,
        usageCount: currentUsage
      };
    }
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// Set the reminder preference
export const setAIReminderPreference = async (userId, showReminder) => {
  try {
    if (!userId) return { success: false, error: 'User not authenticated' };
    
    const userAIUsageRef = doc(db, 'users', userId, 'features', 'ai_generation');
    await updateDoc(userAIUsageRef, {
      showReminder: showReminder
    });
    
    return { success: true };
  } catch (error) {
    // If the document doesn't exist, create it
    try {
      const userAIUsageRef = doc(db, 'users', userId, 'features', 'ai_generation');
      await setDoc(userAIUsageRef, {
        usageCount: 0,
        showReminder: showReminder,
        lastUsed: new Date()
      });
      return { success: true };
    } catch (innerError) {
      return { success: false, error: innerError.message };
    }
  }
};

// Purchase additional AI generations with points
export const purchaseAIGenerations = async (userId, pointCost) => {
  try {
    if (!userId) return { success: false, error: 'User not authenticated' };
    
    // Check if user has enough points
    const userPointsRef = doc(db, 'users', userId, 'stats', 'points');
    const pointsDoc = await getDoc(userPointsRef);
    
    if (!pointsDoc.exists()) {
      return { success: false, error: 'No points record found' };
    }
    
    const userPoints = pointsDoc.data().total || 0;
    
    if (userPoints < pointCost) {
      return { success: false, error: 'Not enough points', requiredPoints: pointCost, userPoints };
    }
    
    // Deduct points
    await updateDoc(userPointsRef, {
      total: increment(-pointCost)
    });
    
    // Grant unlimited access for a period (can be modified to give a specific number instead)
    const userAIFeatureRef = doc(db, 'users', userId, 'features', 'ai_features_unlock');
    const now = new Date();
    const expiryDate = new Date(now);
    expiryDate.setMonth(expiryDate.getMonth() + 2); // 2 months access
    
    await setDoc(userAIFeatureRef, {
      unlocked: true,
      unlockedAt: now,
      expiryDate: expiryDate,
      pointsSpent: pointCost
    }, { merge: true });
    
    return { 
      success: true, 
      remainingPoints: userPoints - pointCost,
      expiryDate: expiryDate
    };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// Generate an AI response to a query
export const generateAIResponse = async (queryContent, userId, queryId = null) => {
  try {
    // Check if the user has access to AI features
    const hasAccess = await hasUnlockedAIFeatures(userId);
    
    if (!hasAccess.hasAccess && !hasAccess.isTrial) {
      return {
        error: true,
        message: "You need more points to access AI features. Keep contributing to earn points!"
      };
    }

    // Check API first
    const serverUp = await isServerRunning();
    
    if (!serverUp) {
      return {
        error: false,
        response: "The AI service seems to be unavailable at the moment. Here's a simulated response: This query raises interesting points that would benefit from community discussion. Consider exploring different perspectives and encouraging participants to share their experiences."
      };
    }

    // If there's no query content, return early
    if (!queryContent || (typeof queryContent === 'string' && queryContent.trim().length === 0)) {
      return {
        error: true,
        message: "No query content to respond to."
      };
    }

    // Format the request payload
    const payload = {
      userId: userId,
      queryId: queryId
    };
    
    if (typeof queryContent === 'string') {
      payload.query = queryContent;
    } else if (typeof queryContent === 'object') {
      payload.title = queryContent.title || "";
      payload.description = queryContent.description || "";
      payload.comments = queryContent.comments || [];
    } else {
      payload.query = String(queryContent);
    }

    // Directly call the correct endpoint
    try {
      const response = await apiClient.post('/api/ai/generate-response', payload);
      
      if (response && response.data) {
        // Increment usage count only on successful generation
        await incrementAIGenerationUsage(userId);
        
        // Format and return the response
        const formattedData = {
          ...response.data,
          response: response.data.response || response.data.summary || "",
          error: false
        };
        
        return formattedData;
      } else {
        throw new Error('Invalid response from server');
      }
    } catch (err) {
      console.error('Error calling /api/ai/generate-response:', err);
      const errorMessage = err.response?.data?.message || err.message || 'Unknown error';
      return {
        error: true,
        message: `Failed to generate response: ${errorMessage}`,
        response: `# AI Generation Failed\n\nCould not generate an AI response due to an error: ${errorMessage}`
      };
    }
  } catch (err) {
    console.error("General error in generateAIResponse:", err);
    return {
      error: true,
      message: `An unexpected error occurred: ${err.message}`,
      response: `# AI Generation Failed\n\nAn unexpected error occurred: ${err.message}`
    };
  }
};

// Generate an AI summary of comments
export const generateAICommentSummary = async (allContent, userId, queryTitle = "", queryId = null) => {
  // Check if the user has access to AI features
  const hasAccess = await hasUnlockedAIFeatures(userId);
  
  if (!hasAccess.hasAccess && !hasAccess.isTrial) {
    return {
      error: true,
      message: "You need more points to access AI features. Keep contributing to earn points!"
    };
  }

  // VERIFY API CLIENT SETUP FIRST
  await verifyApiClientSetup();

  // Check API first
  const serverUp = await isServerRunning();
  
  if (!serverUp) {
    const serverDownSummary = `## Discussion Summary
      
This is a generated summary as the AI service is currently unavailable.

Based on the discussion about "${queryTitle}", there appear to be various perspectives that would normally be analyzed in detail.

The summary would typically highlight:
- Key points raised in the discussion
- Common themes across comments
- Notable perspectives or disagreements
- Potential next steps for this topic

*Note: This is a simulated summary. When the AI service is available, it will provide a detailed analysis of the actual discussion.*`;

    return {
      error: false,
      response: serverDownSummary,
      summary: serverDownSummary
    };
  }

  try {
    // If there are no comments or content, return early
    if (!allContent || allContent.length === 0) {
      return {
        error: true,
        message: "No comments to summarize."
      };
    }

    // Format the comments data for the API
    let commentData;
    if (typeof allContent === 'string') {
      commentData = allContent;
    } else {
      commentData = allContent.map(item => {
        if (item.isReply && item.replyingTo) {
          return `${item.commenter || item.commenterName || 'Anonymous'} (replying to ${item.replyingTo}): "${item.text}" [Votes: ${item.upvoteCount || 0}]`;
        }
        return `${item.commenter || item.commenterName || 'Anonymous'}: "${item.text}" [Votes: ${item.upvoteCount || 0}]`;
      });
    }

    // Directly call the correct endpoint
    try {
      const response = await apiClient.post('/api/ai/generate-summary', {
        comments: commentData,
        userId: userId,
        queryTitle: queryTitle,
        queryId: queryId
      });

      if (response && response.data) {
        // Extract the summary text from the response
        let summaryText = '';
        if (response.data.response) {
          summaryText = response.data.response;
        } else if (response.data.summary) {
          summaryText = response.data.summary;
        } else if (response.data.text) {
          summaryText = response.data.text;
        } else if (response.data.content) {
          summaryText = response.data.content;
        } else if (response.data.mockResponse) {
          summaryText = response.data.mockResponse;
        } else if (typeof response.data === 'string') {
          summaryText = response.data;
        } else {
           // Add a fallback if no expected field is found
          summaryText = "Summary generation completed, but the response format was unexpected.";
        }
        
        // Return the successful response object
        return {
          error: false,
          response: summaryText,
          summary: summaryText // Ensure both fields are present
        };
      } else {
         throw new Error('Invalid response from server');
      }
    } catch (err) {
      console.error('Error calling /api/ai/generate-summary:', err);
      const errorMessage = err.response?.data?.message || err.message || 'Unknown error';
      const fallbackSummary = `# Summary Generation Failed\n\nCould not generate summary for "${queryTitle}" due to an error: ${errorMessage}\n\n*Please try again later.*`;
      return {
        error: false, // Return error:false so frontend doesn't show a harsh error
        message: `Failed to generate summary: ${errorMessage}`,
        response: fallbackSummary,
        summary: fallbackSummary
      };
    }
  } catch (err) {
    console.error("General error in generateAICommentSummary:", err);
    const fallbackSummary = `# Summary Generation Failed\n\nAn unexpected error occurred while generating the summary for "${queryTitle}": ${err.message}\n\n*Please try again later.*`;
    return {
      error: false, // Return error:false so frontend doesn't show a harsh error
      message: `An unexpected error occurred: ${err.message}`,
      response: fallbackSummary,
      summary: fallbackSummary
    };
  }
};